import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Avatar,
  Paper,
  CircularProgress,
  Popper,
  List,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  ClickAwayListener,
  Collapse,
  Tooltip
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import SendIcon from '@mui/icons-material/Send';
import ReplyIcon from '@mui/icons-material/Reply';
import CloseIcon from '@mui/icons-material/Close';
import PushPinIcon from '@mui/icons-material/PushPin';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import designApi, { DesignComment, MentionUser } from '../api/design';

interface DesignChatThreadProps {
  designId: number;
  designNo: string;
  hideHeader?: boolean;
}

// Render comment text with @mentions highlighted inline.
// Pass isMine=true so mentions use a contrasting colour on the blue bubble.
function renderCommentText(text: string, isMine = false): React.ReactNode {
  const parts: React.ReactNode[] = [];
  let lastIdx = 0;
  const regex = /@\[([^\]]+)\]\(user_id:\d+\)/g;
  let match;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIdx) {
      parts.push(text.substring(lastIdx, match.index));
    }
    parts.push(
      <Typography
        key={match.index}
        component="span"
        sx={{
          // On own (blue) bubble use semi-transparent white; on others use primary colour
          color: isMine ? 'rgba(255,255,255,0.85)' : 'primary.main',
          fontWeight: 700,
          fontSize: 'inherit',
          bgcolor: isMine ? 'rgba(255,255,255,0.18)' : 'rgba(25,118,210,0.08)',
          borderRadius: 0.5,
          px: 0.4,
        }}
      >
        @{match[1]}
      </Typography>
    );
    lastIdx = match.index + match[0].length;
  }
  if (lastIdx < text.length) {
    parts.push(text.substring(lastIdx));
  }
  return parts.length > 0 ? <>{parts}</> : text;
}

// Convert internal mention format to display format for the input field
function mentionToDisplay(text: string): string {
  return text.replace(/@\[([^\]]+)\]\(user_id:\d+\)/g, '@$1');
}

// Get current user id from localStorage (same place usePermissions reads it)
function getCurrentUserId(): number | null {
  try {
    const u = JSON.parse(localStorage.getItem('user') || 'null');
    return u?.id ?? null;
  } catch {
    return null;
  }
}

export default function DesignChatThread({ designId, designNo, hideHeader }: DesignChatThreadProps) {
  const currentUserId = getCurrentUserId();
  const [comments, setComments] = useState<DesignComment[]>([]);
  const [users, setUsers] = useState<MentionUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  // Display text (what user sees) vs raw text (with mention markup for API)
  const [displayText, setDisplayText] = useState('');
  const [mentionMap, setMentionMap] = useState<Map<string, { name: string; id: number }>>(new Map());
  
  const [replyTo, setReplyTo] = useState<DesignComment | null>(null);

  // Mention autocomplete state
  const [mentionAnchor, setMentionAnchor] = useState<HTMLElement | null>(null);
  const [mentionSearch, setMentionSearch] = useState('');
  const [mentionStart, setMentionStart] = useState(-1);
  const [mentionHighlightIndex, setMentionHighlightIndex] = useState(0);

  // Pin state
  const [pinnedBannerIndex, setPinnedBannerIndex] = useState(0);
  const commentRefs = useRef<Map<number, HTMLDivElement>>(new Map());

  const inputRef = useRef<HTMLInputElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const fetchComments = useCallback(async () => {
    try {
      const response = await designApi.listComments(designId);
      setComments(response.data || []);
    } catch (err) {
      console.error('Error fetching comments:', err);
    } finally {
      setLoading(false);
    }
  }, [designId]);

  const fetchUsers = useCallback(async () => {
    try {
      const response = await designApi.getCompanyUsers();
      setUsers(response.data || []);
    } catch (err) {
      console.error('Error fetching users:', err);
    }
  }, []);

  useEffect(() => {
    fetchComments();
    fetchUsers();
  }, [fetchComments, fetchUsers]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [comments]);

  // Build raw text with mention markup from display text
  const buildRawText = (display: string, mentions: Map<string, { name: string; id: number }>): string => {
    let raw = display;
    // Replace each @Name with @[Name](user_id:X) — process longest names first to avoid partial matches
    const sortedMentions = Array.from(mentions.entries()).sort((a, b) => b[0].length - a[0].length);
    for (const [displayName, { name, id }] of sortedMentions) {
      const searchStr = `@${displayName}`;
      const replaceStr = `@[${name}](user_id:${id})`;
      raw = raw.split(searchStr).join(replaceStr);
    }
    return raw;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setDisplayText(value);

    // Check for @ mention trigger
    const cursorPos = e.target.selectionStart || 0;
    const textUpToCursor = value.substring(0, cursorPos);
    const lastAtIndex = textUpToCursor.lastIndexOf('@');

    if (lastAtIndex >= 0) {
      const textAfterAt = textUpToCursor.substring(lastAtIndex + 1);
      const charBeforeAt = lastAtIndex > 0 ? value[lastAtIndex - 1] : ' ';
      if ((charBeforeAt === ' ' || charBeforeAt === '\n' || lastAtIndex === 0) && !textAfterAt.includes(' ')) {
        setMentionSearch(textAfterAt.toLowerCase());
        setMentionStart(lastAtIndex);
        setMentionAnchor(inputRef.current);
        setMentionHighlightIndex(0);
        return;
      }
    }
    setMentionAnchor(null);
    setMentionSearch('');
    setMentionHighlightIndex(0);
  };

  const handleMentionSelect = (user: MentionUser) => {
    // Replace the @search text with @Name in display
    const before = displayText.substring(0, mentionStart);
    const cursorPos = mentionStart + mentionSearch.length + 1; // +1 for the @
    const after = displayText.substring(cursorPos);
    const newDisplay = `${before}@${user.name} ${after}`;
    setDisplayText(newDisplay);

    // Track the mention for conversion when sending
    setMentionMap(prev => {
      const next = new Map(prev);
      next.set(user.name, { name: user.name, id: user.id });
      return next;
    });

    setMentionAnchor(null);
    setMentionSearch('');
    setMentionHighlightIndex(0);
    inputRef.current?.focus();
  };

  const handleSend = async () => {
    if (!displayText.trim() || sending) return;
    const rawText = buildRawText(displayText.trim(), mentionMap);
    const parentId = replyTo?.id;

    // Optimistic update — show immediately
    const tempComment: DesignComment = {
      id: Date.now(), // temp id
      design_no: designNo,
      company_id: 0,
      comment: rawText,
      is_pinned: false,
      created_by: 'You',
      created_by_id: null,
      parent_id: parentId || null,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      author: { id: 0, name: 'You', email: '' }
    };
    setComments(prev => [...prev, tempComment]);
    setDisplayText('');
    setMentionMap(new Map());
    setReplyTo(null);

    try {
      setSending(true);
      await designApi.addComment(designId, {
        comment: rawText,
        parent_id: parentId
      });
      // Refresh to get the real comment with proper id/author
      await fetchComments();
    } catch (err) {
      console.error('Error sending comment:', err);
      // Remove optimistic comment on error
      setComments(prev => prev.filter(c => c.id !== tempComment.id));
      setDisplayText(mentionToDisplay(rawText));
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // When mention popup is open, intercept arrow keys, Enter, and Escape
    if (mentionAnchor && filteredMentionUsers.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setMentionHighlightIndex(prev => (prev + 1) % Math.min(filteredMentionUsers.length, 8));
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setMentionHighlightIndex(prev => (prev - 1 + Math.min(filteredMentionUsers.length, 8)) % Math.min(filteredMentionUsers.length, 8));
        return;
      }
      if (e.key === 'Enter') {
        e.preventDefault();
        handleMentionSelect(filteredMentionUsers[mentionHighlightIndex]);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setMentionAnchor(null);
        setMentionSearch('');
        setMentionHighlightIndex(0);
        return;
      }
    }

    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleTogglePin = async (commentId: number, newPinnedState: boolean) => {
    // Optimistic update
    setComments(prev =>
      prev.map(c => (c.id === commentId ? { ...c, is_pinned: newPinnedState } : c))
    );
    try {
      await designApi.pinComment(commentId, newPinnedState);
    } catch (err) {
      console.error('Error toggling pin:', err);
      // Revert on failure
      setComments(prev =>
        prev.map(c => (c.id === commentId ? { ...c, is_pinned: !newPinnedState } : c))
      );
    }
  };

  const scrollToComment = (commentId: number) => {
    const el = commentRefs.current.get(commentId);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const filteredMentionUsers = users.filter(u =>
    u.name.toLowerCase().includes(mentionSearch) || u.email.toLowerCase().includes(mentionSearch)
  );

  // Group comments: top-level and replies
  const topLevel = comments.filter(c => !c.parent_id);
  const repliesMap = new Map<number, DesignComment[]>();
  comments.filter(c => c.parent_id).forEach(c => {
    const arr = repliesMap.get(c.parent_id!) || [];
    arr.push(c);
    repliesMap.set(c.parent_id!, arr);
  });

  // Derived: all pinned comments
  const pinnedComments = comments.filter(c => c.is_pinned);

  // Clamp banner index when pins are removed
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (pinnedComments.length === 0) setPinnedBannerIndex(0);
    else if (pinnedBannerIndex >= pinnedComments.length)
      setPinnedBannerIndex(pinnedComments.length - 1);
  }, [pinnedComments.length]);

  const formatTime = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return 'Just now';
    if (diffMin < 60) return `${diffMin}m`;
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return `${diffHr}h`;
    return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
  };

  if (!designApi.deleteComment) {
    (designApi as any).deleteComment = async (commentId: number) => {
      const instance = (await import('../utils/axios')).default;
      return instance.delete(`/designs/comments/${commentId}`);
    };
  }

  const renderComment = (comment: DesignComment, isReply = false) => {
    // A message is "mine" if it was sent by the current user (by id match or optimistic 'You')
    const isMine =
      (currentUserId !== null && comment.author?.id === currentUserId) ||
      (currentUserId !== null && comment.created_by_id === currentUserId) ||
      comment.created_by === 'You';

    const authorName = comment.author?.name || comment.created_by || '?';

    return (
      <Box
        key={comment.id}
        ref={(el: HTMLDivElement | null) => {
          if (el) commentRefs.current.set(comment.id, el);
          else commentRefs.current.delete(comment.id);
        }}
        sx={{
          display: 'flex',
          flexDirection: isMine ? 'row-reverse' : 'row',
          gap: 1,
          mb: 1.5,
          ml: (!isMine && isReply) ? { xs: 4, sm: 5 } : 0,
          mr: (isMine && isReply) ? { xs: 4, sm: 5 } : 0,
          alignItems: 'flex-end',
          '&:hover .comment-actions': { opacity: 1 },
          // Pinned highlight
          ...(comment.is_pinned && {
            borderLeft: '3px solid',
            borderLeftColor: 'warning.main',
            pl: 0.5,
            bgcolor: alpha('#f59e0b', 0.06),
            borderRadius: 1,
          })
        }}
      >
        {/* Avatar — only shown for others, not for current user */}
        {!isMine && (
          <Avatar sx={{
            width: isReply ? 24 : 30,
            height: isReply ? 24 : 30,
            fontSize: isReply ? '0.65rem' : '0.75rem',
            bgcolor: isReply ? 'grey.400' : 'primary.main',
            flexShrink: 0,
            mb: 0.3
          }}>
            {authorName[0].toUpperCase()}
          </Avatar>
        )}

        {/* Bubble + actions */}
        <Box sx={{ maxWidth: '75%', display: 'flex', flexDirection: 'column', alignItems: isMine ? 'flex-end' : 'flex-start' }}>
          {/* Wrap Paper in relative Box to allow pin badge */}
          <Box sx={{ position: 'relative', display: 'inline-block' }}>
            <Paper
              elevation={0}
              sx={{
                px: 1.5, py: 1,
                borderRadius: 2.5,
                // Pointy corner on the relevant side
                ...(isMine
                  ? { borderBottomRightRadius: 4, bgcolor: 'primary.main' }
                  : { borderBottomLeftRadius: 4, bgcolor: isReply ? alpha('#9e9e9e', 0.12) : alpha('#1976d2', 0.07) }
                ),
                display: 'inline-block',
              }}
            >
              {/* Author name — only shown for others */}
              {!isMine && (
                <Typography
                  variant="caption"
                  fontWeight={700}
                  color={isReply ? 'text.secondary' : 'primary.main'}
                  sx={{ fontSize: '0.72rem', display: 'block', mb: 0.2 }}
                >
                  {authorName}
                </Typography>
              )}
              <Typography
                variant="body2"
                sx={{
                  wordBreak: 'break-word',
                  lineHeight: 1.5,
                  fontSize: '0.84rem',
                  color: isMine ? 'primary.contrastText' : 'text.primary'
                }}
              >
                {renderCommentText(comment.comment, isMine)}
              </Typography>
            </Paper>
            {/* Pin badge — top-right corner of bubble */}
            {comment.is_pinned && (
              <PushPinIcon
                sx={{
                  position: 'absolute',
                  top: -6,
                  right: -6,
                  fontSize: 14,
                  color: 'warning.main',
                  bgcolor: 'background.paper',
                  borderRadius: '50%',
                  p: 0.2,
                  boxShadow: 1,
                }}
              />
            )}
          </Box>

          {/* Timestamp + actions row */}
          <Box
            className="comment-actions"
            sx={{
              display: 'flex',
              flexDirection: isMine ? 'row-reverse' : 'row',
              alignItems: 'center',
              gap: 0.5,
              mt: 0.3,
              opacity: 0,
              transition: 'opacity 0.15s'
            }}
          >
            <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.6rem', px: 0.5 }}>
              {formatTime(comment.created_at)}
            </Typography>
            {/* Pin / unpin button */}
            <Tooltip title={comment.is_pinned ? 'Unpin' : 'Pin message'} placement="top">
              <IconButton
                size="small"
                onClick={() => handleTogglePin(comment.id, !comment.is_pinned)}
                sx={{ p: 0.3, color: comment.is_pinned ? 'warning.main' : 'text.disabled' }}
              >
                {comment.is_pinned
                  ? <PushPinIcon sx={{ fontSize: 18 }} />
                  : <PushPinOutlinedIcon sx={{ fontSize: 18 }} />}
              </IconButton>
            </Tooltip>
            {!isReply && (
              <IconButton size="small" onClick={() => { setReplyTo(comment); inputRef.current?.focus(); }} sx={{ p: 0.3 }}>
                <ReplyIcon sx={{ fontSize: 18 }} />
              </IconButton>
            )}
          </Box>
        </Box>
      </Box>
    );
  };

  // Height of the bottom input area
  const INPUT_BAR_HEIGHT = replyTo ? 110 : 66;

  return (
    <Box sx={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      {!hideHeader && (
        <Box sx={{ px: 2, py: 1, borderBottom: 1, borderColor: 'divider', flexShrink: 0, bgcolor: 'background.paper', zIndex: 1 }}>
          <Typography variant="subtitle2" fontWeight={600}>
            Discussion · {designNo}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {comments.length} message{comments.length !== 1 ? 's' : ''}
          </Typography>
        </Box>
      )}

      {/* Pinned Message Banner */}
      <Collapse in={pinnedComments.length > 0} unmountOnExit>
        <Box sx={{
          px: 1.5, py: 0.75,
          bgcolor: alpha('#f59e0b', 0.12),
          borderBottom: '1px solid',
          borderBottomColor: alpha('#f59e0b', 0.3),
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          flexShrink: 0,
          minHeight: 40,
        }}>
          <PushPinIcon sx={{ fontSize: 14, color: 'warning.dark', flexShrink: 0 }} />

          {/* Clickable preview — scrolls to the message in thread */}
          <Box
            sx={{ flex: 1, overflow: 'hidden', cursor: 'pointer' }}
            onClick={() => { const p = pinnedComments[pinnedBannerIndex]; if (p) scrollToComment(p.id); }}
          >
            <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.65rem', color: 'warning.dark', display: 'block', lineHeight: 1.2 }}>
              Pinned Message
              {pinnedComments.length > 1 && (
                <Typography component="span" variant="caption" sx={{ fontWeight: 400, color: 'text.secondary', ml: 0.75, fontSize: '0.62rem' }}>
                  {pinnedBannerIndex + 1} of {pinnedComments.length}
                </Typography>
              )}
            </Typography>
            <Typography variant="caption" noWrap sx={{ fontSize: '0.75rem', color: 'text.primary', display: 'block' }}>
              {pinnedComments[pinnedBannerIndex]?.comment
                .replace(/@\[([^\]]+)\]\(user_id:\d+\)/g, '@$1')
                .substring(0, 80) ?? ''}
            </Typography>
          </Box>

          {/* Cycle arrows — only when multiple pins */}
          {pinnedComments.length > 1 && (
            <>
              <IconButton size="small" sx={{ p: 0.2 }}
                onClick={() => setPinnedBannerIndex(i => (i - 1 + pinnedComments.length) % pinnedComments.length)}>
                <NavigateBeforeIcon sx={{ fontSize: 16 }} />
              </IconButton>
              <IconButton size="small" sx={{ p: 0.2 }}
                onClick={() => setPinnedBannerIndex(i => (i + 1) % pinnedComments.length)}>
                <NavigateNextIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </>
          )}

          {/* Unpin currently shown message */}
          <Tooltip title="Unpin this message" placement="top">
            <IconButton size="small" sx={{ p: 0.2, color: 'text.secondary' }}
              onClick={() => { const p = pinnedComments[pinnedBannerIndex]; if (p) handleTogglePin(p.id, false); }}>
              <CloseIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Collapse>

      {/* Messages — takes remaining space above the input bar */}
      <Box
        ref={scrollRef}
        sx={{
          flex: 1,
          overflowY: 'auto',
          px: { xs: 1.5, sm: 2 },
          py: 1.5,
          pb: `${INPUT_BAR_HEIGHT + 8}px`,
          bgcolor: '#fafbfc',
          WebkitOverflowScrolling: 'touch'
        }}
      >
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={24} />
          </Box>
        ) : comments.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <Typography variant="body2" color="text.disabled">No messages yet</Typography>
            <Typography variant="caption" color="text.disabled">Start the conversation!</Typography>
          </Box>
        ) : (
          topLevel.map(comment => (
            <React.Fragment key={comment.id}>
              {renderComment(comment)}
              {(repliesMap.get(comment.id) || []).map(reply => renderComment(reply, true))}
            </React.Fragment>
          ))
        )}
      </Box>

      {/* Bottom input — pinned to bottom absolutely */}
      <Box sx={{
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        bgcolor: 'background.paper',
        borderTop: 1,
        borderColor: 'divider',
        zIndex: 2
      }}>
        {/* Reply indicator */}
        {replyTo && (
          <Box sx={{
            px: 2, py: 0.5, bgcolor: alpha('#1976d2', 0.06),
            display: 'flex', alignItems: 'center', gap: 1,
            borderLeft: '3px solid', borderLeftColor: 'primary.main'
          }}>
            <ReplyIcon sx={{ fontSize: 14, color: 'primary.main' }} />
            <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }} noWrap>
              Replying to <strong>{replyTo.author?.name || replyTo.created_by}</strong>
            </Typography>
            <IconButton size="small" onClick={() => setReplyTo(null)} sx={{ p: 0.3 }}>
              <CloseIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Box>
        )}

        {/* Input bar */}
        <Box sx={{
          px: { xs: 1, sm: 1.5 },
          pt: 0.75,
          pb: { xs: 'max(env(safe-area-inset-bottom, 0px), 12px)', sm: '12px' },
          display: 'flex',
          gap: 1,
          alignItems: 'flex-end'
        }}>
          <TextField
            inputRef={inputRef}
            fullWidth
            size="small"
            placeholder="Type a message... @ to mention"
            value={displayText}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            multiline
            maxRows={4}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 3,
                fontSize: '0.875rem',
                bgcolor: '#f5f5f5',
                '& fieldset': { borderColor: 'transparent' },
                '&:hover fieldset': { borderColor: 'divider' },
                '&.Mui-focused fieldset': { borderColor: 'primary.main', borderWidth: 1 }
              }
            }}
          />
          <IconButton
            color="primary"
            onClick={handleSend}
            disabled={!displayText.trim() || sending}
            sx={{
              mb: 0.3,
              bgcolor: displayText.trim() ? 'primary.main' : 'transparent',
              color: displayText.trim() ? 'white' : 'text.disabled',
              width: 36, height: 36,
              '&:hover': { bgcolor: displayText.trim() ? 'primary.dark' : 'transparent' },
              '&.Mui-disabled': { bgcolor: 'transparent' }
            }}
          >
            {sending ? <CircularProgress size={18} color="inherit" /> : <SendIcon sx={{ fontSize: 18 }} />}
          </IconButton>
        </Box>
      </Box>

      {/* Mention Autocomplete */}
      <Popper
        open={Boolean(mentionAnchor) && filteredMentionUsers.length > 0}
        anchorEl={mentionAnchor}
        placement="top-start"
        sx={{ zIndex: 1400 }}
        modifiers={[{ name: 'offset', options: { offset: [0, 8] } }]}
      >
        <ClickAwayListener onClickAway={() => setMentionAnchor(null)}>
          <Paper sx={{ maxHeight: 200, overflowY: 'auto', width: 260, boxShadow: 6, borderRadius: 2 }}>
            <List dense sx={{ p: 0.5 }}>
              {filteredMentionUsers.slice(0, 8).map((user, idx) => (
                <ListItemButton
                  key={user.id}
                  onClick={() => handleMentionSelect(user)}
                  selected={idx === mentionHighlightIndex}
                  sx={{
                    py: 0.75,
                    borderRadius: 1,
                    '&.Mui-selected': { bgcolor: 'primary.main', color: 'white', '&:hover': { bgcolor: 'primary.dark' } },
                    '&.Mui-selected .MuiTypography-root': { color: 'inherit' },
                  }}
                >
                  <ListItemAvatar sx={{ minWidth: 36 }}>
                    <Avatar sx={{
                      width: 26, height: 26, fontSize: '0.7rem',
                      bgcolor: idx === mentionHighlightIndex ? 'rgba(255,255,255,0.3)' : 'primary.main'
                    }}>{user.name[0]}</Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={<Typography variant="body2" fontWeight={500}>{user.name}</Typography>}
                    secondary={<Typography variant="caption" color={idx === mentionHighlightIndex ? 'rgba(255,255,255,0.75)' : 'text.disabled'} sx={{ fontSize: '0.65rem' }}>{user.email}</Typography>}
                  />
                </ListItemButton>
              ))}
            </List>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </Box>
  );
}
